import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface WorkingProductChoice {
  id: string
  /** "DIN / code", as the bar names it. */
  label: string
}

/**
 * "Working on" at the top of the "P" menu (z8nrz7fxub): which of the
 * procedure's products in hand this form is about. One or several; Save
 * stores the choice on the form. Shown only when there is a choice to make —
 * a single product in hand is named without asking.
 */
export function WorkingProductPicker({
  choices,
  selectedIds,
  needsChoice,
  onSave,
}: {
  choices: WorkingProductChoice[]
  selectedIds: string[]
  needsChoice: boolean
  onSave: (ids: string[]) => Promise<void>
}) {
  const [draft, setDraft] = useState<string[]>(selectedIds)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const unchanged = draft.length === selectedIds.length && draft.every(id => selectedIds.includes(id))

  function toggle(id: string) {
    setDraft(d => (d.includes(id) ? d.filter(x => x !== id) : [...d, id]))
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      await onSave(draft)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the choice.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='border-b' data-testid='working-product-picker'>
      <p
        className={cn(
          'px-3 pt-2 text-xs font-semibold uppercase tracking-wide',
          needsChoice ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        Working on
      </p>
      <p className='px-3 pb-1 text-xs text-muted-foreground'>
        {needsChoice ? 'Choose the product or products this form is about.' : 'The product or products this form is about.'}
      </p>
      <ul className='max-h-48 overflow-y-auto py-1'>
        {choices.map(c => (
          <li key={c.id}>
            <label className='flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent'>
              <input
                type='checkbox'
                className='h-4 w-4 accent-primary'
                checked={draft.includes(c.id)}
                onChange={() => toggle(c.id)}
              />
              <span className='truncate font-mono text-xs'>{c.label}</span>
            </label>
          </li>
        ))}
      </ul>
      {error && <p className='px-3 pb-1 text-xs text-destructive'>{error}</p>}
      <div className='flex justify-end px-3 pb-2'>
        <Button size='sm' onClick={() => void save()} disabled={saving || draft.length === 0 || unchanged}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  )
}
